import { useCallback, useMemo } from 'react';

import { useFocusEffect } from 'expo-router';

import {
  LoyaltyCmsType,
  useGetLoyaltyQuestsQuery,
  useLoyaltyServiceQuestsQuery,
} from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import logger from 'utils/logger';

import { createQuest, createServiceQuest, validateQuest } from './create-quest';
import { IQuest, IUseLoyaltyQuestQuery } from './types';

export const useLoyaltyQuestQuery = ({
  loyaltyId,
  cmsType,
  refecthOnFocus,
}: {
  loyaltyId: string;
  cmsType?: LoyaltyCmsType;
  refecthOnFocus?: boolean;
}): IUseLoyaltyQuestQuery => {
  const enableLoyaltyServiceQuest = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_QUEST));
  const legacyQuery = useGetLoyaltyQuestsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      loyaltyId,
      where: {
        activeOrEligible: true,
      },
    },
    skip: !loyaltyId || enableLoyaltyServiceQuest,
  });
  const serviceQuery = useLoyaltyServiceQuestsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        activeOrEligible: true,
      },
    },
    skip: !loyaltyId || !enableLoyaltyServiceQuest,
  });
  const { refetch: refetchLegacyQuests } = legacyQuery;
  const { refetch: refetchServiceQuests } = serviceQuery;

  const legacyQuests = legacyQuery.data?.loyaltyUserV2?.quests;
  const serviceQuests = serviceQuery.data?.loyaltyServiceQuests?.items;
  const quest = useMemo(() => {
    const createdQuests = enableLoyaltyServiceQuest
      ? serviceQuests?.map(createServiceQuest)
      : legacyQuests?.map(createQuest);

    if (!createdQuests) {
      return null;
    }

    // create and validate quests
    // invalid quests should be filtered out
    const questList = createdQuests.map(createdQuest => {
      const questErrors = validateQuest(createdQuest);

      if (questErrors.length) {
        logger.warn(`Quest validation ${JSON.stringify(questErrors)}`);
        return null;
      }

      return createdQuest;
    });

    // filter invalid quests
    return questList.find(
      (quest: IQuest | null): quest is IQuest =>
        quest !== null &&
        (cmsType
          ? quest.loyaltyCmsType === cmsType
          : quest.loyaltyCmsType !== LoyaltyCmsType.USER_JOURNEY)
    );
  }, [cmsType, enableLoyaltyServiceQuest, legacyQuests, serviceQuests]);

  const refetchQuests = useCallback(() => {
    if (!loyaltyId) {
      return Promise.resolve();
    }

    if (enableLoyaltyServiceQuest) {
      return refetchServiceQuests({ input: { activeOrEligible: true } });
    }

    return refetchLegacyQuests({ loyaltyId, where: { activeOrEligible: true } });
  }, [enableLoyaltyServiceQuest, loyaltyId, refetchLegacyQuests, refetchServiceQuests]);

  useFocusEffect(
    useCallback(() => {
      if (refecthOnFocus) {
        void refetchQuests();
      }
    }, [refecthOnFocus, refetchQuests])
  );

  const activeQuery = enableLoyaltyServiceQuest ? serviceQuery : legacyQuery;

  return {
    loadingQuests: activeQuery.loading,
    quest: quest || null,
    error: activeQuery.error,
    refetchQuests,
  };
};
